import { AxiosError } from 'axios';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form } from 'reactstrap';

import { requestNotificationPermission } from '@app/firebaseMessaging';
import { loginRequest, updateFcmTokenRequest } from '@reducers/auth/AuthAction';
import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import { CustomButton } from '@shared/buttons';
import AsyncButtonLink from '@shared/buttons/AsyncButtonLink/AsyncButtonLink';
import Alert from '@shared/components/alert/Alert';
// import DividerWithText from '@shared/components/divider/DividerWithText';
import InputField, { InputFieldProps } from '@shared/components/input/InputField';
// import AuthTabNavigation from '@shared/components/navigation/AuthTabNavigation';
import useTranslation from '@shared/hooks/useTranslation';

import sanitizeReturnTo from '@shared/helpers/navigation';

import ForgotPasswordModal from './components/forgot-password-modal/ForgotPasswordModal';
import './LoginPage.scss';

interface FormFields {
  email: string;
  password: string;
}

const LoginPage = () => {
  const [ isLoggingIn, setLoggingIn ] = useState(false);
  const [ errorMessage, setErrorMessage ] = useState<string | null>(null);
  const i18n = useTranslation('general');
  const i18nAuth = useTranslation('login');
  const dispatch = useDispatch<any>();
  const location = useLocation();
  // Prefer the `?returnTo=` param that ClientRouter / the 401 interceptor
  // put in the redirect URL — it is the reliable, per-redirect source of
  // truth for where the user was headed. Fall back to the stored lastPage.
  // This fixes email deep links (e.g. /referral-messages/{id}) landing on
  // Home instead of the intended referral after a logged-out user signs in.
  const queryReturnTo = new URLSearchParams(location.search).get('returnTo');
  const returnTo = sanitizeReturnTo(queryReturnTo || localStorage.getItem('lastPage'));
  const [ isForgotPasswordModalVisible, setForgotPasswordModalVisiblility ] = useState(false);
  const navigate = useNavigate();

  const [ formData, setFormData ] = useState<FormFields>({
    email: '',
    password: '',
  });

  const FORM_FIELDS: Array<InputFieldProps & { name: keyof FormFields }> = useMemo(() => [
    {
      id: 'login-input-email',
      name: 'email',
      label: i18n.input.email.label,
      placeholder: i18n.input.email.placeholder,
      type: 'text',
      showFeedback: false,
      value: formData?.email,
    },
    {
      id: 'login-input-password',
      name: 'password',
      label: i18n.input.password.label,
      placeholder: i18n.input.password.placeholder,
      type: 'password',
      isPasswordStrength: false,
      showFeedback: false,
      value: formData?.password,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [ formData ]);

  const handleForgotPasswordToggle = useCallback(
    () => setForgotPasswordModalVisiblility(prev => !prev),
    []);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      await dispatch(loginRequest({ ...formData })).$promise;
      // if the user type is not equal to the last user type, do not redirect to return to
      // to avoid error pages from rendering
      navigate(returnTo);
      // show notification permission
      requestNotificationPermission((token: string) => dispatch(
        updateFcmTokenRequest({ fcm_token: token })),
      );
    } catch (error) {
      const { response: errorResponse } = error as AxiosError;

      if (errorResponse) {
        if (errorResponse.status === 422) {
          setErrorMessage(errorResponse.data?.message);
        } else if (errorResponse.status === 401) {
          setErrorMessage(i18n.validation.incorrect_credentials);
        } else {
          setErrorMessage(errorResponse.data?.error || errorResponse.data?.message);
        }
        return;
      }
      handleError(error);
    } finally {
      setLoggingIn(false);
    }
  };

  const createAccount = () => {
    navigate('/auth/register');
  };

  // check required fields if has input
  const isLoginDisabled = useMemo(() => Object.values(formData).some((value) => (
    isEmpty(value)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [ formData ]);

  return (
    <div className="login-page">
      <div className="s1 title">
        {i18nAuth.title}
      </div>
      <div className="subtitle">
        {i18nAuth.subtitle}
      </div>
      <div className="d-grid">
        {!isEmpty(errorMessage) && <Alert color="error">{errorMessage}</Alert>}
        <Form className="d-grid" onSubmit={handleLogin}>
          {FORM_FIELDS.map((field) => (
            <InputField
              {...field}
              key={field.id}
              fieldError={errorMessage as string}
              onInputChangeHandler={(value) => setFormData((prevState: any) => ({
                ...prevState,
                [field.name]: value,
              }))}
              formGrpClassName="login-form-grp-style"
            />
          ))}
          <div className="text-start">
            <AsyncButtonLink key="login-forgot-password" className="btn-link" onClick={handleForgotPasswordToggle}>{i18nAuth.button.forgot_password}</AsyncButtonLink>
          </div>
          <CustomButton
            key="btn-login-submit"
            btnRef={null}
            className="btn-lg login-btn-style"
            disabled={isLoggingIn || isLoginDisabled}
            loading={isLoggingIn}
            color="primary"
            type="submit"
          >
            {i18nAuth.button.login}
          </CustomButton>
        </Form>
        <div className="create-account-container">
          {
            LocaleService.parseTranslationElement(i18nAuth.button.create_account, {
              // eslint-disable-next-line react/no-unstable-nested-components
              createAccount: (label: string) => (
                <AsyncButtonLink key="login-create-account" className="btn-link create-account-btn" onClick={createAccount}>
                  {label}
                </AsyncButtonLink>
              ),
            })
          }
        </div>
      </div>
      <ForgotPasswordModal
        toggle={handleForgotPasswordToggle}
        open={isForgotPasswordModalVisible}
      />
    </div>
  );
};

export default LoginPage;
